import React, { useState, useEffect, useMemo } from 'react';
import { Play } from 'lucide-react';
import { PreKSettings } from './types';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import CountdownAnimation from '../ui/CountdownAnimation';
import { usePreKAudio, SELECTION_SCREEN_AUDIO } from '../../hooks/usePreKAudio';
import PreKJourneyPath from '../PreKPractice/shared/JourneyPath';
import KJourneyPath from '../KPractice/shared/JourneyPath';
import { recordPageTransition } from '../../config/api';

interface SelectionScreenProps {
  onStart: (settings: PreKSettings) => void;
  gradeLevel?: 'preK' | 'K';
  currentTrack?: string;
}

const SelectionScreen: React.FC<SelectionScreenProps> = ({ onStart, gradeLevel = 'preK', currentTrack }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [assessmentId, setAssessmentId] = useState<string>('');
  const [showCountdown, setShowCountdown] = useState(false);
  const { playAudio } = usePreKAudio();


  useEffect(() => {
    const initializeServices = async () => {
      if (!user?.userId) return;

      try {
        setIsFetching(true);

        // Record page transition with correct trackId on SelectionScreen load
        const trackId = currentTrack || user?.focusTrack || 'TRACK_PREK_NAMING';
        sessionStorage.setItem('activeTrackId', trackId);
        recordPageTransition({
          userId: user.userId,
          trackId: trackId,
          page: 'assessment',
        }).catch((error) => {
          console.error('Error recording page transition:', error);
        });

        // For now, generate a mock assessment ID
        const mockAssessmentId = `kindergarten-assessment-${Date.now()}`;
        setAssessmentId(mockAssessmentId);

        // Play welcome audio when component loads
        playAudio(SELECTION_SCREEN_AUDIO.welcome).catch(() => {
          // This is expected - browsers block autoplay without user interaction
        });

      } catch (error) {
        console.error('Failed to fetch Pre-K assessment data:', error);
      } finally {
        setIsFetching(false);
      }
    };

    initializeServices();
  }, [user?.userId, currentTrack, playAudio]);

  // Add keyboard event listener for Enter key and click event for any click
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' &&
          !isLoading &&
          !isFetching &&
          assessmentId) {
        handleStart();
      }
    };

    const handleClick = () => {
      if (!isLoading &&
          !isFetching &&
          assessmentId) {
        handleStart();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('click', handleClick);
    };
  }, [isLoading, isFetching, assessmentId]);

  const handleStart = async () => {
    if (!assessmentId) return;

    setIsLoading(true);
    setShowCountdown(true);
  };

  const handleCountdownComplete = () => {
    try {
      if (!assessmentId) return;

      onStart({
        assessmentType: 'diagnostic_fluency',
        subtests: [],
        assessmentId,
        speechEnabled: true
      });
    } catch (error) {
      console.error('Failed to start Pre-K assessment:', error);
      setIsLoading(false);
      setShowCountdown(false);
    }
  };


  return (
    <div className="relative min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
      {showCountdown && (
        <CountdownAnimation onComplete={handleCountdownComplete} />
      )}

      {/* Journey Path - positioned at top of screen */}
      <div className="absolute top-6 left-0 right-0">
        {gradeLevel === 'preK' ? (
          <PreKJourneyPath currentTrack={currentTrack ?? user?.focusTrack} />
        ) : (
          <KJourneyPath currentTrack={currentTrack ?? user?.focusTrack} />
        )}
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        <h1 className="text-6xl font-bold text-center mb-12 animate-fade-in">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Let's Play!
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center relative"
        >
          {/* Pointing Finger */}
          <motion.div
            animate={{
              x: [0, 10, 0],
              scale: [1, 1.1, 1],
              rotate: -15
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -left-32 top-1/2 transform -translate-y-1/2 text-8xl hidden md:block"
          >
            👉
          </motion.div>

          <motion.button
            whileHover={{ scale: 1.08, rotate: 1 }}
            whileTap={{ scale: 0.92 }}
            animate={{
              scale: [1, 1.1, 1],
              y: [0, -8, 0]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            onClick={handleStart}
            disabled={isLoading || isFetching || !assessmentId}
            className={`h-20 lg:h-24 px-16 rounded-3xl font-bold text-2xl lg:text-3xl shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu flex items-center justify-center gap-4 mx-auto
              ${(isLoading || isFetching || !assessmentId) && 'opacity-50 cursor-not-allowed'}`}
          >
            {!isLoading && !isFetching && <Play className="w-12 h-12" />}
            <span>{isLoading ? 'Starting...' : isFetching ? 'Getting Ready...' : 'START'}</span>
            {(isLoading || isFetching) && (
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
            )}
          </motion.button>

          {/* Pointing finger for smaller screens */}
          <motion.div
            animate={{
              y: [0, -5, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="mt-8 text-6xl md:hidden"
          >
            👆
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SelectionScreen;